import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { codKpiCardSx, focusRingSx, NJIM } from "../../theme/njimTheme";

export type CodStatTone = "warning" | "success" | "danger" | "info" | "ink";

const TONE: Record<CodStatTone, { color: string; wash: string }> = {
  warning: { color: NJIM.warning, wash: NJIM.warningWash },
  success: { color: NJIM.success, wash: NJIM.successWash },
  danger: { color: NJIM.danger, wash: NJIM.dangerWash },
  info: { color: NJIM.info, wash: NJIM.infoWash },
  ink: { color: NJIM.ink, wash: NJIM.mintWash },
};

interface CodStatCardProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tone?: CodStatTone;
  loading?: boolean;
  onClick?: () => void;
  /** Use a smaller value font for long values such as money amounts. */
  compactValue?: boolean;
}

const CodStatCard: React.FC<CodStatCardProps> = ({
  label,
  value,
  icon,
  tone = "ink",
  loading = false,
  onClick,
  compactValue = false,
}) => {
  const palette = TONE[tone];
  const clickable = typeof onClick === "function";

  return (
    <Box
      component={clickable ? "button" : "div"}
      onClick={onClick}
      sx={{
        ...codKpiCardSx,
        ...(clickable
          ? {
              cursor: "pointer",
              font: "inherit",
              textAlign: "start",
              width: "100%",
              ...focusRingSx,
            }
          : null),
      }}
    >
      {icon ? (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            bgcolor: palette.wash,
            color: palette.color,
            flexShrink: 0,
            "& svg": { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: NJIM.textSecondary,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={label}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton width="55%" height={32} />
        ) : (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: compactValue ? { xs: 16, md: 18 } : { xs: 22, md: 26 },
              fontWeight: 800,
              color: NJIM.ink,
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CodStatCard;
